import React, { ReactElement } from 'react';

import { Route } from '@philips-emr/hpms-stipulator-commons';
import StipulatorContext from '@philips-emr/hpms-stipulator-commons/lib/contexts/stipulator-context';
import FilterService from '@philips-emr/hpms-stipulator-commons/lib/services/filter-service';
import { HpmsCustomCardEvent, HpmsCustomCardMenuRenderEvent } from '@philips-emr/hpms-stipulator-components';
import { HPMSOnCreateMenu, HPMSOnCreateOption, HPMSOnOptionClick } from '@philips-emr/hpms-stipulator-components/lib/events/event';
import { FetchAPI, Stamp, StampIcon } from '@philips-emr/tws-components';
import { Color } from '@philips-emr/tws-constants';
import { Value } from '@philips-emr/tws-core';
import Controller, { OnBeforeFetch, OnDynamicContentRender, OnMount, OnUnmount } from '@philips-emr/tws-core/lib/client';
import { MountEvent, RenderEvent } from '@philips-emr/tws-core/lib/component/types';
import { ModalService } from '@philips-emr/tws-core/lib/services';
import { ModalProps } from '@philips-emr/tws-core/lib/services/modal-service';

import { ConsultaMigracaoContratoAliases as Aliases, FragmentNames } from '../codegen';

import BaseController from './BaseController';

enum StatusMigracao {
  ABERTA = 1,
  FINALIZADA = 2,
  REJEITADA = 3,
  CANCELADA = 4,
}

interface Data {
  dsStatus: string;
  ieStatus: StatusMigracao;
  nrSeqContratoMig: number;
  nrSeqSegurado: number;
}

const OptionKey = {
  OBSERVACAO: 'observacao',
  EDITAR: 'editar',
};

@Controller({ fragment: FragmentNames.CONSULTA_MIGRACAO_CONTRATO })
class ConsultarMigracaoContratoController extends BaseController {

  @OnMount(Aliases.FILTER_CONSULTA)
  onFilterMount(event: MountEvent): void {
    FilterService.restoreFilterState(event);
  }

  @OnUnmount(Aliases.FILTER_CONSULTA)
  onUnmountFilter(event: MountEvent): void {
    FilterService.persistFilterState(event);
  }

  @OnBeforeFetch(Aliases.FETCH_CONSULTA)
  onBeforeFetchBeneficiarios(event: MountEvent): void {
    const fetch = event.target as unknown as FetchAPI;
    const { nrSeqSubestipulante } = StipulatorContext.getCurrentContract();
    nrSeqSubestipulante && fetch.setParameters({ ...fetch.getParameters(), nrSeqSubestipulante });
  }

  @HPMSOnCreateMenu(Aliases.HPMS_CUSTOM_CARD)
  onCreateCardMenu(event: HpmsCustomCardMenuRenderEvent<Record<string, Value>>): void {
    const { data, shouldRender } = event;
    const { ieStatus } = data as unknown as Data;

    if (![StatusMigracao.ABERTA, StatusMigracao.REJEITADA].includes(ieStatus)) {
      shouldRender(false);
    }
  }

  @HPMSOnCreateOption(Aliases.HPMS_CUSTOM_CARD)
  onCreateOption(event: HpmsCustomCardMenuRenderEvent<Record<string, Value>>): void {
    const { data, key, shouldRender } = event;
    const { ieStatus } = data as unknown as Data;

    const show = {
      [OptionKey.OBSERVACAO]: ieStatus == StatusMigracao.REJEITADA,
      [OptionKey.EDITAR]: ieStatus == StatusMigracao.ABERTA,
    };

    shouldRender(show[key]);
  }

  @HPMSOnOptionClick(Aliases.HPMS_CUSTOM_CARD)
  onOptionClick(event: HpmsCustomCardEvent<Record<string, Value>>): void {
    const { key } = event;
    const { nrSeqContratoMig, nrSeqSegurado } = event.data as unknown as Data;

    const action = {
      [OptionKey.OBSERVACAO]: () => ModalService.openDefault(this.modalObservacao(nrSeqContratoMig)),
      [OptionKey.EDITAR]: () => {
        const { nrSeqGrupoContratoWeb, nrSeqContrato } = this.historyService.getCurrentRouteParams();
        this.historyService.push(Route.MIGRAR_CONTRATO, { nrSeqBeneficiario: nrSeqSegurado, nrSeqGrupoContratoWeb: nrSeqGrupoContratoWeb, nrSeqContrato: nrSeqContrato });
      },
    };

    if (action[key]) {
      action[key]();
    }
  }

  @OnDynamicContentRender(Aliases.HPMS_CUSTOM_CARD)
  onDynamicContentRenderCardLote(event: RenderEvent): void {
    const { data, render } = event;
    const { ieStatus, dsStatus } = data as Data;
    const content = (): ReactElement => (this.getStampByStatus(ieStatus, dsStatus));
    render(content);
  }

  private modalObservacao(nrSeqMigracao: number): ModalProps {

    const { nrSeqContrato } = this.historyService.getCurrentRouteParams();
    const modalWithFragment: ModalProps = {
      closeable: true,
      fragment: 'observacao-migracao-contrato',
      title: 'stipulator.solicitacao-rejeitada-pela-operadora',
      contextData: { nrSeqContrato, nrSeqMigracao },
    };
    return modalWithFragment;
  }

  private getIconColorByStatus(ieStatus: StatusMigracao): Color {
    const color = {
      [StatusMigracao.ABERTA]: Color.SAFETY_NEUTRAL,
      [StatusMigracao.FINALIZADA]: Color.SAFETY_GREEN,
      [StatusMigracao.REJEITADA]: Color.SAFETY_RED,
      [StatusMigracao.CANCELADA]: Color.SAFETY_NEUTRAL,
    };

    return color[ieStatus];
  }

  private getIconTypeByStatus(ieStatus: StatusMigracao): StampIcon {
    const icon = {
      [StatusMigracao.ABERTA]: StampIcon.CLOCK,
      [StatusMigracao.FINALIZADA]: StampIcon.CHECK,
      [StatusMigracao.REJEITADA]: StampIcon.CANCELED,
      [StatusMigracao.CANCELADA]: StampIcon.CANCELED,
    };

    return icon[ieStatus];
  }

  private getStampByStatus(ieStatus: StatusMigracao, dsStatus: string): ReactElement {
    return (
      <Stamp icon={this.getIconTypeByStatus(ieStatus)} color={this.getIconColorByStatus(ieStatus)} label={dsStatus} />
    );
  }

}
export default ConsultarMigracaoContratoController;
